import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useIsAuthenticated } from '@/entities/session';
import { env } from '@/shared/config';
import { useInactivityTracker } from '@/shared/lib';
import { fonts } from '@/shared/theme';
import { Icon } from '@/shared/ui';
import { useLogout } from '../model/use-logout';

/** Signs the user out after the configured idle time, with a 10-second warning first. */
export function InactivityLogout() {
  const { t } = useTranslation();
  const isAuthenticated = useIsAuthenticated();
  const logout = useLogout();
  const { warning, secondsLeft, totalSeconds, stayActive } = useInactivityTracker({
    enabled: isAuthenticated,
    inactivityTimeout: env.inactivityTimeoutMs,
    onTimeout: () => logout.mutate(),
  });

  return (
    <Dialog open={warning && isAuthenticated} maxWidth="xs" fullWidth aria-describedby="idle-desc">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={(theme) => ({ display: 'flex', color: theme.vars.palette.md.warnInk })}>
          <Icon name="clock" />
        </Box>
        {t('inactivity.title')}
      </DialogTitle>
      <DialogContent>
        <Typography id="idle-desc" variant="body2" color="text.secondary">
          {t('inactivity.description', { seconds: secondsLeft })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 2.5, mb: 1.25 }}>
          <Typography
            sx={{ fontFamily: fonts.head, fontWeight: 800, fontSize: 34, lineHeight: 1 }}
            aria-live="assertive"
          >
            {secondsLeft}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('inactivity.seconds')}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          color="warning"
          value={(secondsLeft / totalSeconds) * 100}
          aria-label={t('inactivity.timeRemaining')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => logout.mutate()} disabled={logout.isPending}>
          {t('inactivity.logoutNow')}
        </Button>
        <Button variant="contained" onClick={stayActive} autoFocus>
          {t('inactivity.stayLoggedIn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
